import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { EvaluationStep } from '../types';

const FIRST_STEP = 0;
const STEP_OFFSET = 1;
const NO_SEQUENCE = 0;
const NOT_FOUND = -1;
const RESULT_STEP_NAME = 'result';

function bySequence(first: EvaluationStep, second: EvaluationStep): number {
	const result = (first.sequence ?? NO_SEQUENCE) - (second.sequence ?? NO_SEQUENCE);

	return result;
}

function isResult(step?: EvaluationStep): boolean {
	const result = step?.name === RESULT_STEP_NAME;

	return result;
}

export function useEvaluationSteps(stepsList: EvaluationStep[], initialSequence?: number, reachedSequence?: number) {
	const [currentStep, setCurrentStep] = useState<number>(FIRST_STEP);

	const [furthestStep, setFurthestStep] = useState<number>(FIRST_STEP);

	const hasInitializedStep = useRef<boolean>(false);

	const steps = useMemo(() => {
		const result = [...stepsList].sort(bySequence);

		return result;
	}, [stepsList]);

	const lastStep = Math.max(steps.length - STEP_OFFSET, FIRST_STEP);

	const lastSequence = steps[lastStep]?.sequence ?? NO_SEQUENCE;

	const currentStepData = steps[currentStep];

	const isFirstStep = currentStep === FIRST_STEP;

	const isResultStep = isResult(currentStepData);

	const sequenceToIndex = useCallback(
		(sequence: number): number => {
			const exactIndex = steps.findIndex((step) => step.sequence === sequence);

			if (exactIndex !== NOT_FOUND) {
				return exactIndex;
			}

			const nextIndex = steps.findIndex((step) => (step.sequence ?? NO_SEQUENCE) > sequence);

			if (nextIndex !== NOT_FOUND) {
				return nextIndex;
			}

			const fallback = Math.max(steps.length - STEP_OFFSET, FIRST_STEP);

			return fallback;
		},
		[steps]
	);

	useEffect(() => {
		if (hasInitializedStep.current || !steps.length || initialSequence === undefined) {
			return;
		}

		hasInitializedStep.current = true;

		const initialStep = sequenceToIndex(initialSequence);
		const reachedStep = sequenceToIndex(reachedSequence ?? initialSequence);

		setCurrentStep(initialStep);
		setFurthestStep(Math.max(initialStep, reachedStep));
	}, [steps, initialSequence, reachedSequence, sequenceToIndex]);

	function clampStep(step: number): number {
		const boundedStep = Math.max(step, FIRST_STEP);
		const result = Math.min(boundedStep, lastStep);

		return result;
	}

	const nextStep = clampStep(currentStep + STEP_OFFSET);

	const previousStep = clampStep(currentStep - STEP_OFFSET);

	function goTo(step: number): number {
		const reachedStep = clampStep(step);

		setCurrentStep(reachedStep);
		setFurthestStep((furthest) => Math.max(furthest, reachedStep));

		return reachedStep;
	}

	function rewindProgress(): void {
		setFurthestStep(currentStep);
	}

	function goNext(): void {
		goTo(currentStep + STEP_OFFSET);
	}

	function goPrevious(): void {
		goTo(currentStep - STEP_OFFSET);
	}

	function sequenceAt(step: number): number | undefined {
		const result = steps[clampStep(step)]?.sequence;

		return result;
	}

	const furthestSequence = sequenceAt(furthestStep);

	const isComplete = furthestStep === lastStep;

	return {
		steps,
		currentStep,
		furthestStep,
		furthestSequence,
		isComplete,
		currentStepData,
		isFirstStep,
		isResultStep,
		lastStep,
		lastSequence,
		nextStep,
		previousStep,
		goTo,
		goNext,
		goPrevious,
		rewindProgress,
		sequenceAt
	};
}
